import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box, Button, Grid } from "@mui/material";
import { ReactComponent as Logo } from "assets/icons/logo_tate.svg";
import IntroIllustrationImage from "assets/images/intro-illustration.svg";
import LogoImage from "assets/images/logo.svg";
import { ContentMain } from "components/Parts/Organisms/ContentMain";
import React from "react";
import styles from "./LoginPage.module.scss";
import { useLoginHooks } from "./hooks";

export const LoginPage = React.memo(() => {
  const {
    dialogRef,
    frameRef,
    onDialogCancel,
    onDialogClick,
    onIntroVideoCloseClick,
    onIntroVideoOpenClick
  } = useLoginHooks();

  return (
    <ContentMain>
      <div className={[styles.pageLayout, styles.pageLayoutReverse].join(" ")}>
        <section className={styles.intro} aria-labelledby="intro-title">
          <span
            className={[styles.introDeco, styles.introDeco1].join(" ")}
            aria-hidden="true"
          ></span>
          <span
            className={[styles.introDeco, styles.introDeco2].join(" ")}
            aria-hidden="true"
          ></span>
          <span
            className={[styles.introDeco, styles.introDeco3].join(" ")}
            aria-hidden="true"
          ></span>
          <span
            className={[styles.introDeco, styles.introDecoDots].join(" ")}
            aria-hidden="true"
          ></span>

          <div className={styles.introInner}>
            <img
              className={styles.introLogo}
              src={LogoImage}
              alt="スモーラ"
              width="100"
              height="71"
            />

            <h1 className={styles.introTitle} id="intro-title">
              <span className={styles.introPc}>
                不動産業務を、
                <br />
                もっとスマートに。
              </span>
              <span className={styles.introSp}>
                不動産情報を、
                <br />
                もっとシンプルに。
              </span>
            </h1>

            <p className={styles.introLead}>
              <span className={styles.introPc}>
                物件情報の整理や共有を、地図ベースでわかりやすく。
                <br />
                スモーラの特徴や活用方法をご紹介しています。
              </span>
              <span className={styles.introSp}>
                地図で見える。すぐに使える。
                <br />
                不動産業務のDXツール「スモーラ」
              </span>
            </p>

            <img
              className={styles.introVisual}
              src={IntroIllustrationImage}
              alt=""
              width="760"
              height="300"
            />

            <div className={styles.introActions}>
              <a
                className={[styles.introBtn, styles.introBtnPrimary].join(" ")}
                href="https://info.sumora.jp/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={styles.introPc}>初めての方はこちら！</span>
                <span className={styles.introSp}>サービスを詳しく見る</span>
                <svg
                  className={styles.introPc}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M7 4l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <svg
                  className={styles.introSp}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10h14M12 5l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <button
                type="button"
                className={[styles.introBtn, styles.introBtnSecondary].join(
                  " "
                )}
                aria-haspopup="dialog"
                onClick={() =>
                  onIntroVideoOpenClick(
                    "https://youtube.com/shorts/ugjbKRy2bNI?feature=share"
                  )
                }
              >
                説明動画を見る！
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <circle
                    cx="10"
                    cy="10"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path d="M8 6.5v7l5.5-3.5z" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        <dialog
          ref={dialogRef}
          className={styles.introVideo}
          id="intro-video"
          aria-label="説明動画"
          onCancel={onDialogCancel}
          onClick={onDialogClick}
        >
          <div className={styles.introVideoInner}>
            <button
              type="button"
              className={styles.introVideoClose}
              aria-label="閉じる"
              onClick={onIntroVideoCloseClick}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className={styles.introVideoFrame} ref={frameRef}></div>
          </div>
        </dialog>

        <div className={styles.authPane}>
          <div className={styles.loginForm}>
            <Authenticator
              hideSignUp
              components={{
                Header: () => {
                  return (
                    <>
                      <Box className={styles.headerBar}>
                        <Logo />
                      </Box>
                    </>
                  );
                },
                Footer: () => {
                  return (
                    <Grid
                      className={styles.footerBar}
                      container
                      sx={{ justifyContent: "center" }}
                      spacing={2}
                    >
                      <Grid>
                        <Button
                          href="https://sumora.info/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          スモーラについて
                        </Button>
                      </Grid>
                      <Grid>
                        <Button href="/about">注意事項</Button>
                      </Grid>
                      <Grid>
                        <Button href="/license">ライセンス表記</Button>
                      </Grid>
                    </Grid>
                  );
                }
              }}
              formFields={{
                forceNewPassword: {
                  password: {
                    label: "新しいパスワード",
                    placeholder: "新しいパスワードを入力してください"
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </ContentMain>
  );
});
